import React, { useState } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/shared/ui/dropdown-menu"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { DateInput } from "@/shared/components/DateInput"
import { FilterIcon, SearchIcon } from '@/shared/components/SvgIcons'
import TextInput from '@/shared/components/TextInput'
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import AddReportDialog from "./AddReportDialog";
import closeCircle from '@/images/x-circle.png';
import { useCurrentTool } from "@/shared/hooks/useCurrentTool"
import useReportFilters from "./useReportFilters";
import { useReportsContext } from '@/features/reports/ReportsContext';

function SearchAndFilter() {
  const { t } = useTranslation();
  const { currentToolFields } = useCurrentTool();

  const [searchIconClicked, setSearchIconClicked] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);

  const { setPage } = useReportsContext();

  const {
    filterValues,
    searchUserName,
    setSearchUserName,
    handleFilterSearch,
    handleFilterInputChange,
    resetFilter,
    handleSearchUserName,
    handleFormSubmit,
    countFiltersApplied
  } = useReportFilters();

  const count = countFiltersApplied();

  const handleCloseIcon = () => {
    setSearchUserName("");
    setSearchIconClicked(false);
    searchUserName && handleFilterSearch({
      search: "",
      reset: false,
      resetSearchBar: true,
      searchUserName,
      page: 1,
    });
    setPage(1);
  };

  const getTextInput = () => {
    return (
      <TextInput
        searchClicked={searchIconClicked}
        name="search_name"
        placeholder={`${t("actions.search")}...`}
        value={searchUserName}
        onChange={(e) => handleSearchUserName(e, setPage)}
        type="text"
        className='w-fit'
        useWidthOnMobile={false}
        {...(!searchUserName
          ? {}
          : {
            postIcon: (
              <img
                src={closeCircle}
                alt=""
                height={16}
                width={16}
                className="cursor-pointer hidden md:block"
                onClick={handleCloseIcon}
              />
            ),
          })}
      />
    );
  };

  const getFilterModel = () => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="bg-white flex flex-col max-w-[315px] p-4 sm:rounded-[15px] w-full sm:max-h-min sm:min-h-min"
      >
        <div className="flex w-full">
          <form
            onSubmit={(e) => handleFormSubmit(e, setPage)}
            className="flex flex-col gap-[10px] w-full h-full sm:justify-center"
          >
            <div className="grid grid-cols-2 w-full gap-[10px] justify-center items-center">
              <DateInput
                name="startDate"
                value={filterValues?.startDate || ""}
                onChange={handleFilterInputChange}
                placeholder={t("reports.filters.dateFrom")}
              />
              <DateInput
                name="endDate"
                value={filterValues?.endDate || ""}
                onChange={handleFilterInputChange}
                placeholder={t("reports.filters.dateTo")}
              />
            </div>
            <div className="flex flex-col gap-[10px] w-full">
              {currentToolFields?.map((field) => (
                <Input
                  key={field.name}
                  name={field.name}
                  placeholder={t(`fields.${field.name}`)}
                  type="text"
                  value={filterValues?.[field.name] || ""}
                  onChange={handleFilterInputChange}
                />
              ))}
              <Input
                textArea={true}
                name={"comment"}
                placeholder={t("common.comment")}
                type="text"
                value={filterValues?.comment || ""}
                onChange={handleFilterInputChange}
              />
            </div>
            <div className="flex flex-col justify-center gap-[10px] w-full">
              <Button
                type="submit"
                className="font-SuisseIntlLight font-medium"
              >
                {t("reports.filters.apply")}
              </Button>
              {!!count && (
                <Button
                  type="button"
                  variant="outline"
                  className="font-SuisseIntlLight font-semibold text-black"
                  onClick={() => resetFilter(setPage)}
                >
                  {t("reports.filters.reset")}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-[4px] flex items-center gap-3 sm:mt-0 w-full justify-end">
      {!searchIconClicked ? (
        <div className="flex justify-end gap-3">
          <div className="hidden relative md:left-3 md:flex h-[42px] ease-in-out transition-all">
            {getTextInput()}
          </div>
          <div>
            <button
              className="flex ml-[10px] sm:ml-0 md:hidden items-center gap-[10px] text-[14px] justify-start border-[#E5E7EC] text-[#525866] border-[1px] rounded-[10px] py-[10px] px-[12px] w-[44px] h-[42px]"
              onClick={() => {
                setSearchIconClicked(true);
              }}
            >
              <SearchIcon />
            </button>
          </div>
          <DropdownMenu isOpen={filtersModalOpen} onOpenChange={setFiltersModalOpen}>
            <DropdownMenuTrigger asChild>
              <div>
                <Button
                  className={`hidden md:flex h-full text-[14px] rounded-[10px] ${count && 'font-semibold'} text-center justify-center items-center`}
                  variant="outline"
                >
                  <span className="mt-[1px]">{t("reports.filters.text")} {count ? '(' + count + ')' : ''}</span>
                </Button>
                <Button
                  variant="outline"
                  className={`block h-full relative md:hidden items-center gap-[10px] ${filtersModalOpen && 'bg-slate-100'}`}
                >
                  <div>
                    {count ? (
                      <span className="circle mobile right-0 bottom-0 absolute">
                        <span>{count}</span>
                      </span>
                    ) : null}
                    <FilterIcon />
                  </div>
                </Button>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='rounded-[14px] mt-[6px]'>
              {getFilterModel()}
            </DropdownMenuContent>
          </DropdownMenu>

          <AddReportDialog variant="desktop" />
        </div>
      ) : (
        <div className="flex gap-3">
          {getTextInput()}
          <button
            className=" border-[#E5E7EC] border-[1px] rounded-[10px] px-2 h-[42px]"
            onClick={handleCloseIcon}
          >
            <X className="text-[#525866]" />
          </button>
        </div>
      )}
    </div>
  );
}

export default SearchAndFilter;
